/**
 * The simulator panel's state model.
 *
 * Design doc: 2026-08-11-mobile-simulator-integration §16.7, §16.8.
 *
 * Kept out of the component on purpose. These are decisions, not pixels — "a
 * stale frame must never render as live" and "every error names its exact
 * remediation" are assertable without a browser, and a state model buried in
 * JSX only ever gets exercised by clicking.
 *
 * §16.7's finding was that a binary ready/not-ready is wrong: readiness is a
 * progressive checklist, and four states were missing from the original seven —
 * setup, per-device consent, crash-retry, and a terminal give-up state, which is
 * the UI face of a BLOCKED lease.
 */

export type SimulatorPanelState =
  // Readiness checklist — progressive, not binary.
  | "checking-setup"
  | "checking-xcode"
  | "checking-runtime"
  | "setup-incomplete"
  // Per-device consent, before anything is allocated.
  | "awaiting-consent"
  // Allocation.
  | "queued"
  | "allocating"
  | "booting"
  // Running.
  | "live"
  | "reconnecting"
  // Failure.
  | "crash-retrying"
  | "blocked";

export const SIMULATOR_PANEL_STATES: readonly SimulatorPanelState[] = [
  "checking-setup",
  "checking-xcode",
  "checking-runtime",
  "setup-incomplete",
  "awaiting-consent",
  "queued",
  "allocating",
  "booting",
  "live",
  "reconnecting",
  "crash-retrying",
  "blocked",
];

/**
 * Legal transitions. Data rather than branches, so "no state outside the model"
 * is checkable by reading the table instead of auditing the component.
 */
const TRANSITIONS: Readonly<Record<SimulatorPanelState, readonly SimulatorPanelState[]>> = {
  "checking-setup": ["checking-xcode", "setup-incomplete"],
  "checking-xcode": ["checking-runtime", "setup-incomplete"],
  "checking-runtime": ["awaiting-consent", "setup-incomplete"],
  "setup-incomplete": ["checking-setup"],
  "awaiting-consent": ["queued", "allocating"],
  queued: ["allocating", "blocked"],
  allocating: ["booting", "crash-retrying", "blocked"],
  booting: ["live", "crash-retrying", "blocked"],
  live: ["reconnecting", "crash-retrying", "blocked"],
  reconnecting: ["live", "crash-retrying", "blocked"],
  "crash-retrying": ["allocating", "blocked"],
  // Terminal. The UI face of a BLOCKED lease: it does not offer retry, because
  // the lease behind it is non-claimable and retrying would lie about that.
  blocked: [],
};

export function canTransition(from: SimulatorPanelState, to: SimulatorPanelState): boolean {
  return TRANSITIONS[from]?.includes(to) ?? false;
}

export function isTerminal(state: SimulatorPanelState): boolean {
  return TRANSITIONS[state].length === 0;
}

/** Retry is offered only where it is honest. */
export function offersRetry(state: SimulatorPanelState): boolean {
  return state === "crash-retrying" || state === "setup-incomplete";
}

/** States that pulse. Every one must pair with motion-reduce (§16.5). */
export function isPending(state: SimulatorPanelState): boolean {
  return (
    state === "checking-setup" ||
    state === "checking-xcode" ||
    state === "checking-runtime" ||
    state === "queued" ||
    state === "allocating" ||
    state === "booting" ||
    state === "reconnecting" ||
    state === "crash-retrying"
  );
}

export interface PanelCopy {
  title: string;
  /**
   * What to do about it. §16.8: every error names its exact remediation, and
   * where a command is involved it appears verbatim so it can be pasted.
   */
  remediation?: string;
}

/**
 * Copy per state.
 *
 * The remediation strings are deliberately specific. "Something went wrong"
 * teaches a user nothing and costs a support round-trip; naming the command
 * ends it. Where the outcome is genuinely uncertain the copy says so rather
 * than claiming failure — a simulator that has not finished booting may still
 * be booting.
 */
const COPY: Readonly<Record<SimulatorPanelState, PanelCopy>> = {
  "checking-setup": { title: "Checking your simulator setup…" },
  "checking-xcode": { title: "Checking for Xcode…" },
  "checking-runtime": { title: "Checking for the simulator runtime…" },
  "setup-incomplete": {
    title: "Simulator support is not ready on this host",
    remediation:
      "The iOS Simulator platform is missing. Open Xcode → Settings → Components and download iOS, or run `xcodebuild -downloadPlatform iOS` on the host.",
  },
  "awaiting-consent": {
    title: "Let this project use a simulator?",
    remediation:
      "A simulator will be allocated on a connected Mac host and released when you are done.",
  },
  queued: {
    title: "Waiting for a free simulator…",
    remediation:
      "Every simulator on this host is in use. The lease starts as soon as one is released.",
  },
  allocating: { title: "Allocating a simulator…" },
  booting: { title: "Booting the simulator…" },
  live: { title: "Simulator is live" },
  reconnecting: {
    title: "Reconnecting to the simulator…",
    remediation: "The stream dropped. The lease is still held, so the device keeps its state.",
  },
  "crash-retrying": {
    title: "The simulator stopped and is restarting",
    remediation:
      "Retrying automatically. If it keeps happening the lease will stop retrying and report why.",
  },
  blocked: {
    title: "This simulator has stopped retrying after repeated failures",
    remediation:
      "The lease is blocked and will not be reused. Release it and start a new session; the host keeps the record for diagnosis.",
  },
};

export function copyFor(state: SimulatorPanelState): PanelCopy {
  return COPY[state];
}

/** Exposed for the coverage test; a state without copy must fail loudly. */
export const panelCopyStates = Object.keys(COPY) as SimulatorPanelState[];

/**
 * Whether the stream may be presented as live.
 *
 * Derived from frame freshness rather than from a boolean someone sets. §11 is
 * explicit that a stale frame must never render as live: a wedged simulator
 * keeps its last frame on screen, and a panel that trusts a `isLive` flag shows
 * a still image as a working device — which is worse than showing nothing,
 * because the user acts on it.
 */
export function isStreamLive(input: {
  state: SimulatorPanelState;
  lastFrameAtMs: number | null;
  nowMs: number;
  stalenessThresholdMs?: number;
}): boolean {
  if (input.state !== "live") return false;
  if (input.lastFrameAtMs === null) return false;
  const threshold = input.stalenessThresholdMs ?? 2_000;
  return input.nowMs - input.lastFrameAtMs <= threshold;
}

/**
 * Human control is desktop-only (§16.1, D1).
 *
 * A 402pt device scaled into a 390px viewport renders app controls at ~38px, so
 * the 48px touch contract is unmeetable by arithmetic. Per DESIGN.md a
 * pointer-only affordance is hidden rather than shrunk.
 */
export function canTakeControl(input: {
  viewportWidthPx: number;
  state: SimulatorPanelState;
}): boolean {
  const MD_BREAKPOINT_PX = 768;
  return input.viewportWidthPx >= MD_BREAKPOINT_PX && input.state === "live";
}
